import * as _ from "lodash";
import React, { useEffect } from "react";
import { FixedSizeList } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import { DetailBaseData, Match } from "../../../Models";
import * as Const from "../../../Constants";
import CustomScrollArea from "../../General/ScrollArea";
import NavigationItem from "./NavigationItem";
import Loader from "../../General/loader";
import * as Models from "../../../Models";
import { isBuildType } from "../../../Helpers/build";
import filterItemsStore from "../../../Store/filterItems";
import { fetchFilters } from "../../../Helpers/azure";
import * as fromAzureApi from "../../../Api/azure";

interface NavigationContainerProps {
    match: Match;
    location: Models.Location;
    isModalSelectionMode: boolean;
    editableField?: Models.EditableItemField;
}

export default function NavigationContainer(props: NavigationContainerProps) {
    const { params } = props.match;
    const { filter, parameter } = params;

    const {
        getActiveFilterItems,
        getActiveFilterItemsRootIds,
        setFiltersLoaded,
        navigationItemSelected,
        getDetails,
        openAllParentsForId,
    } = filterItemsStore();
    const filtersLoaded = filterItemsStore((s) => s.filtersLoaded);
    const searchItemIds = filterItemsStore((s) => s.searchItemIds);
    const filterItems = getActiveFilterItems(params);
    const isCountrySearch = filterItemsStore((s) => s.isCountrySearch);

    useEffect(() => {
        if (filtersLoaded) return;
        initSidebar();
    }, []);

    const initSidebar = async () => {
        const filterPromises: any[] = [];
        const productTypes: Models.DetailBaseDataCollection = (await getDetails(
            fetchFilters(Const.PRODUCTS_TREE)
        )) as Models.DetailBaseDataCollection;
        _.forEach(productTypes?.items, (product: Models.DetailBaseData) => {
            filterPromises.push(
                getDetails(fromAzureApi.getProductTypes(product.id))
            );
        });
        filterPromises.push(getDetails(fetchFilters(Const.SITE_TREE)));
        filterPromises.push(getDetails(fetchFilters(Const.CUSTOMERS_TREE)));
        await Promise.all(filterPromises).then(() => {
            setFiltersLoaded();
            if (parameter) {
                openAllParentsForId(parameter);
            }
        });
    };

    function getItems(isSearch: boolean): DetailBaseData[] {
        const { isModalSelectionMode } = props;

        let sortedFilterItems: DetailBaseData[];
        let sortRootItems: boolean = !isSearch || (isSearch && isCountrySearch);

        if (sortRootItems) {
            sortedFilterItems = _.sortBy(
                _.uniqBy(filterItems, (c: DetailBaseData) => c.id),
                ["description"]
            );
        } else {
            sortedFilterItems = filterItems;
        }

        if (isSearch) {
            sortedFilterItems = sortedFilterItems.filter((el) => {
                return (
                    searchItemIds.indexOf(el.id) !== -1 &&
                    (((el.type === "Site" || isBuildType(el.type)) &&
                        filter === Const.SITE_TREE &&
                        !isModalSelectionMode) ||
                        (!isBuildType(el.type) &&
                            el.type !== "Equipment" &&
                            isModalSelectionMode) ||
                        filter === Const.CUSTOMERS_TREE ||
                        filter === Const.PRODUCTS_TREE)
                );
            });
        }

        if (filter === Const.CUSTOMERS_TREE) return sortedFilterItems;

        if (!isModalSelectionMode) {
            const activeFilterItemsRootIds = getActiveFilterItemsRootIds(
                isSearch ? { filter: "search" } : params
            );
            sortedFilterItems = _.filter(sortedFilterItems, (item) => {
                return (
                    _.find(activeFilterItemsRootIds, (id) => item.id === id) !==
                    undefined
                );
            });
        } else {
            const siteItems: DetailBaseData[] = sortedFilterItems.filter(
                (el) => el.type === "Site"
            );
            if (siteItems.length > 0) {
                sortedFilterItems = siteItems;
            }
        }

        return sortedFilterItems;
    }

    const { isModalSelectionMode } = props;
    const search: string = props.location.search;
    const isSearch: boolean =
        search.indexOf("search") !== -1 || isModalSelectionMode;
    const items: DetailBaseData[] = getItems(isSearch);
    const level: number = 0;
    const shouldAutoScroll: boolean = true;

    const scrollTo: (element: FixedSizeList) => void = (
        element: FixedSizeList
    ) => {
        const selectedItemId: string = props.match.params.parameter;

        if (element && selectedItemId) {
            const selectedItemIndex: number = items.findIndex(
                (i) => i.id === selectedItemId
            );
            element.scrollToItem(selectedItemIndex, "smart");
        }
    };

    if (!filtersLoaded) {
        return <Loader />;
    }

    function navigationItemSelectedHandler(
        node: DetailBaseData,
        shouldCloseMobileMenu?: boolean
    ) {
        navigationItemSelected(
            node,
            props.match.params,
            true,
            props.isModalSelectionMode,
            shouldCloseMobileMenu
        );
    }

    if (filter === Const.CUSTOMERS_TREE) {
        const Row = ({ index, style }) => {
            const item: DetailBaseData = items[index];

            return (
                <div style={style}>
                    <NavigationItem
                        key={item.id}
                        navigationItems={filterItems}
                        navigationItem={item}
                        navigationItemSelected={navigationItemSelectedHandler}
                        filter={props.match.params.filter}
                        isModalSelectionMode={props.isModalSelectionMode}
                        level={level}
                    />
                </div>
            );
        };

        return (
            <React.Fragment>
                <div id="treeview" className="NavigationTree with-autosizer">
                    {isSearch && (
                        <div className="NavigationTree__item--initialMarginLeft NavigationTree__item--searchResults">
                            Search results:
                        </div>
                    )}
                    <div className="NavigationTree--autosizer">
                        <AutoSizer>
                            {({ height, width }) => (
                                <FixedSizeList
                                    ref={scrollTo}
                                    outerElementType="ul"
                                    height={height}
                                    itemSize={60}
                                    itemCount={items.length}
                                    width={width}
                                >
                                    {Row}
                                </FixedSizeList>
                            )}
                        </AutoSizer>
                    </div>
                </div>
            </React.Fragment>
        );
    }

    return (
        <div id="treeview" className="NavigationTree">
            <CustomScrollArea shouldAutoScroll={shouldAutoScroll}>
                {isSearch && (
                    <div className="NavigationTree__item--initialMarginLeft NavigationTree__item--searchResults">
                        Search results:
                    </div>
                )}
                <ul>
                    {_.map(items, (navigationItem: DetailBaseData) => (
                        <NavigationItem
                            key={navigationItem.id}
                            navigationItems={filterItems}
                            navigationItem={navigationItem}
                            navigationItemSelected={
                                navigationItemSelectedHandler
                            }
                            filter={props.match.params.filter}
                            isModalSelectionMode={props.isModalSelectionMode}
                            level={level}
                        />
                    ))}
                </ul>
            </CustomScrollArea>
        </div>
    );
}
